import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Trash2, 
  Copy, 
  Settings, 
  Database, 
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import { sampleDataManager, SampleDataStatus } from '@/utils/sampleDataManager';
import { useToast } from '@/hooks/use-toast';

export const SampleDataManager: React.FC = () => {
  const [status, setStatus] = useState<SampleDataStatus | null>(null);
  const [userDataStatus, setUserDataStatus] = useState<{
    hasUserData: boolean;
    hasTemporaryData: boolean;
    materialityTopics: number;
    stakeholders: number;
    reports: number;
    settings: boolean;
    insights: number;
    engagements: number;
    temporaryTopics: number;
    temporaryStakeholders: number;
    temporaryReports: number;
    temporarySettings: boolean;
    temporaryInsights: number;
    temporaryEngagements: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [clearingUserData, setClearingUserData] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkDataStatus();
  }, []);

  const checkDataStatus = async () => {
    try {
      setLoading(true);
      const [sampleStatus, userStatus] = await Promise.all([
        sampleDataManager.checkSampleDataStatus(),
        sampleDataManager.checkUserDataStatus()
      ]);
      setStatus(sampleStatus);
      setUserDataStatus(userStatus);
    } catch (error) {
      console.error('Error checking data status:', error);
      toast({
        title: 'Error',
        description: 'Failed to check data status',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopySampleData = async (force: boolean = false) => {
    try {
      setCopying(true);
      const result = await sampleDataManager.copySampleDataToOrganization(force);
      
      if (result.success) {
        if (result.error && result.error.includes('already has materiality data')) {
          toast({
            title: 'Info',
            description: 'You already have materiality data. Use "Force Add" to overwrite existing data.',
          });
        } else {
          toast({
            title: 'Success',
            description: 'Sample data added for exploration. You can now explore the application features with sample data.',
          });
        }
        await checkDataStatus();
        
        // Trigger refresh of Materiality Assessment data
        if (typeof window !== 'undefined' && (window as any).refreshMaterialityData) {
          (window as any).refreshMaterialityData();
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to add sample data',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error adding sample data:', error);
      toast({
        title: 'Error',
        description: 'Failed to add sample data',
        variant: 'destructive'
      });
    } finally {
      setCopying(false);
    }
  };

  const handleInitializeSettings = async () => {
    try {
      setInitializing(true);
      const result = await sampleDataManager.initializeDefaultSettings();
      
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Default settings initialized successfully.',
        });
        await checkDataStatus();
        
        // Trigger refresh of Materiality Assessment data
        if (typeof window !== 'undefined' && (window as any).refreshMaterialityData) {
          (window as any).refreshMaterialityData();
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to initialize default settings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error initializing settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize default settings',
        variant: 'destructive'
      });
    } finally {
      setInitializing(false);
    }
  };

  const handleClearUserData = async () => {
    try {
      setClearingUserData(true);
      const result = await sampleDataManager.clearUserData();
      
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Temporary sample data has been cleared. Your real organization data has been preserved.',
        });
        await checkDataStatus();
        
        // Trigger refresh of Materiality Assessment data
        if (typeof window !== 'undefined' && (window as any).refreshMaterialityData) {
          (window as any).refreshMaterialityData();
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to clear user data',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error clearing user data:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear user data',
        variant: 'destructive'
      });
    } finally {
      setClearingUserData(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Sample Data Management
          </CardTitle>
          <CardDescription>
            Managing sample data for Materiality Assessment module
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Sample Data Management
        </CardTitle>
        <CardDescription>
          Manage sample data for the Materiality Assessment module. You can clear sample data to start fresh or copy it to your organization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Current Status</h3>
          
          {/* Sample Data Status */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Sample Data Available</h4>
            {status?.hasSampleData ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Sample Topics</p>
                    <p className="text-sm text-muted-foreground">{status.materialityTopics} topics</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">Sample Stakeholders</p>
                    <p className="text-sm text-muted-foreground">{status.stakeholders} stakeholders</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium">Sample Reports</p>
                    <p className="text-sm text-muted-foreground">{status.reports} reports</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Info className="h-5 w-5 text-gray-600" />
                <div>
                  <p className="font-medium">No Sample Data</p>
                  <p className="text-sm text-muted-foreground">
                    No sample data is currently available in the system.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* User Data Status */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Your Organization Data</h4>
            {userDataStatus?.hasUserData ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-indigo-600" />
                  <div>
                    <p className="font-medium">Your Topics</p>
                    <p className="text-sm text-muted-foreground">{userDataStatus.materialityTopics} topics</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="font-medium">Your Stakeholders</p>
                    <p className="text-sm text-muted-foreground">{userDataStatus.stakeholders} stakeholders</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-violet-600" />
                  <div>
                    <p className="font-medium">Your Reports</p>
                    <p className="text-sm text-muted-foreground">{userDataStatus.reports} reports</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <Info className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium">No Organization Data</p>
                  <p className="text-sm text-muted-foreground">
                    You haven't created any materiality assessment data for your organization yet.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Actions</h3>
          <div className="flex flex-wrap gap-3">
            {/* Add Sample Data */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={copying}>
                  <Copy className="h-4 w-4 mr-2" />
                  {copying ? 'Adding...' : 'Add Sample Data'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Sample Data</DialogTitle>
                  <DialogDescription>
                    This will add sample materiality assessment data to your organization for exploration. 
                    This includes sample topics, stakeholders, reports, and settings.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900">What will be added:</h4>
                    <ul className="mt-2 text-sm text-blue-800 space-y-1">
                      <li>• 12 sample materiality topics</li>
                      <li>• 10 sample stakeholders</li>
                      <li>• 4 sample reports</li>
                      <li>• Default settings configuration</li>
                      <li>• Sample insights and engagements</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium text-yellow-900">Note:</h4>
                    <p className="text-sm text-yellow-800">
                      This will create temporary sample data for exploration. You can clear it later when ready to use real data.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => handleCopySampleData(false)} disabled={copying}>
                    {copying ? 'Adding...' : 'Add Sample Data'}
                  </Button>
                  <Button variant="outline" onClick={() => handleCopySampleData(true)} disabled={copying}>
                    Force Add
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Initialize Settings */}
            <Button 
              variant="outline" 
              onClick={handleInitializeSettings}
              disabled={initializing || status?.settings}
            >
              <Settings className="h-4 w-4 mr-2" />
              {initializing ? 'Initializing...' : status?.settings ? 'Settings Configured' : 'Initialize Settings'}
            </Button>

            {/* Clear Your Data */}
            {userDataStatus?.hasUserData && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={clearingUserData}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {clearingUserData ? 'Clearing...' : 'Clear Your Data'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear Your Organization Data</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove all data you have created for your organization. Sample data will remain available.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <div className="p-3 bg-red-50 rounded-lg">
                      <h4 className="font-medium text-red-900">Your data to be removed:</h4>
                      <ul className="text-sm text-red-800 space-y-1 mt-2">
                        <li>• Your materiality topics ({userDataStatus.materialityTopics})</li>
                        <li>• Your stakeholders ({userDataStatus.stakeholders})</li>
                        <li>• Your reports ({userDataStatus.reports})</li>
                        <li>• Your insights ({userDataStatus.insights})</li>
                        <li>• Your engagements ({userDataStatus.engagements})</li>
                        <li>• Your settings configuration</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-900">Sample data will be preserved:</h4>
                      <p className="text-sm text-green-800">
                        Sample data will remain available for you and other users to reference.
                      </p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> This action will permanently delete your organization data. You can always add sample data again for exploration.
                      </p>
                    </div>
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleClearUserData}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {clearingUserData ? 'Clearing...' : 'Clear Your Data'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Information</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Sample Data</h4>
              <p className="text-sm text-blue-800">
                Sample data provides examples of materiality topics, stakeholders, and reports to help you understand how to use the module. Sample data is always available and cannot be cleared.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Your Data</h4>
              <p className="text-sm text-green-800">
                You can clear your organization's data to start fresh. This only removes your data, not the sample data that remains available for all users.
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">Settings</h4>
              <p className="text-sm text-purple-800">
                Initialize default settings to configure your organization's preferences for materiality assessments.
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <h4 className="font-medium text-orange-900 mb-2">Data Safety</h4>
              <p className="text-sm text-orange-800">
                All data is stored securely in your organization's database. Only you and your team members can access it.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 